export const OAUTH_ACCOUNT_NOT_LINKED_ERROR = "OAuthAccountNotLinked" as const;

export const NEXT_AUTH_SIGN_IN_ERRORS = [
  "Signin",
  "OAuthSignin",
  "OAuthCallback",
  "OAuthCreateAccount",
  "EmailCreateAccount",
  "Callback",
  OAUTH_ACCOUNT_NOT_LINKED_ERROR,
  "EmailSignin",
  "CredentialsSignin",
  "SessionRequired",
  "default",
] as const;

export type NextAuthSignInError = (typeof NEXT_AUTH_SIGN_IN_ERRORS)[number];

const nextAuthSignInErrorSet = new Set<string>(NEXT_AUTH_SIGN_IN_ERRORS);

const NEXT_AUTH_SIGN_IN_ERROR_MESSAGES: Record<NextAuthSignInError, string> = {
  Signin: "Sign-in failed. Try again.",
  OAuthSignin: "OAuth sign-in failed. Try signing in with a different account.",
  OAuthCallback:
    "OAuth callback failed. Try signing in with a different account.",
  OAuthCreateAccount: "Could not create account with OAuth provider.",
  EmailCreateAccount: "Could not create account with email sign-in.",
  Callback: "Authentication callback failed. Please try again.",
  OAuthAccountNotLinked:
    "This Google account is not linked yet. Sign in with your original method first, then connect Google from Account settings.",
  EmailSignin: "The sign-in email could not be sent. Please try again.",
  CredentialsSignin:
    "Sign-in failed. Check the details you provided and try again.",
  SessionRequired: "Please sign in to access this page.",
  default: "Authentication failed. Please try again.",
};

export function isNextAuthSignInError(
  error: string | null | undefined
): error is NextAuthSignInError {
  return !!error && nextAuthSignInErrorSet.has(error);
}

export function getNextAuthSignInErrorMessage(
  error: string | null | undefined
): string | null {
  if (!(error && isNextAuthSignInError(error))) {
    return null;
  }

  return NEXT_AUTH_SIGN_IN_ERROR_MESSAGES[error];
}

const NEXT_AUTH_ERROR_PAGE_ERRORS = [
  "Configuration",
  "AccessDenied",
  "Verification",
  "default",
] as const;

export type NextAuthErrorPageError =
  (typeof NEXT_AUTH_ERROR_PAGE_ERRORS)[number];

const nextAuthErrorPageErrorByLower: Record<string, NextAuthErrorPageError> = {
  configuration: "Configuration",
  accessdenied: "AccessDenied",
  verification: "Verification",
  default: "default",
};

export const NEXT_AUTH_ERROR_PAGE_MESSAGES: Record<
  NextAuthErrorPageError,
  string
> = {
  Configuration:
    "A server authentication configuration error occurred. Please try again later.",
  AccessDenied:
    "Sign-in request was denied. Please retry with the correct account.",
  Verification:
    "Verification link is invalid or expired. Please request a new one.",
  default: "An unknown error occurred during authentication.",
};

export function normalizeNextAuthErrorPageError(
  error: string | null | undefined
): NextAuthErrorPageError {
  if (!error) {
    return "default";
  }

  return nextAuthErrorPageErrorByLower[error.toLowerCase()] ?? "default";
}
